import { ParsedUrlQuery } from 'querystring';

import { KeyboardEvent, useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useParams, useSearchParams } from 'next/navigation';

import {
  SearchViewModel,
  MetaViewModel,
  HotelListFiltersViewModel,
  PackageListHotelViewModel,
} from '@api/v3';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { SearchIcon } from '@assets/icons';
import {
  ResponsiveBox,
  ResponsiveSearchBoxEdit,
  ResponsiveSearchBoxEditLabel,
  ResponsiveSearchBoxItem,
  ResponsiveSearchBoxLabel,
  ResponsiveSearchBoxWrapper,
  ResponsiveSearchBoxSubLabel,
  ResponsiveBoxAffix,
  SearchIconWrapper,
  ResponsiveBoxContainer,
} from '@components/views/hotel/listing/HotelListingStyle';
import CustomIcon from '@components/utils/CustomIcon';
import { DateType, formatMomentDate } from '@utils/dateUtil';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import StickyElement from '@components/utils/StickyElement';
import { useDevice } from '@contexts/device';
import { getLoyaltyTypeFromUtmSource } from '@components/ui/loyalty-verifications/helpers';
import SearchReservedId from '@components/ui/SearchReservedId';
import { useKocAliemContext } from '@contexts/loyalty/KocAilem';
import Typography from '@components/Typography';
import { callService } from '@api/remote-call';
import { useSearchURL } from '@api/data-loaders/resource';
import KocAilemBanner from '@components/views/hotel/listing/components/KocAilem/KocAilemBanner';
import { useFindAirportByAirportCode } from '@components/views/flight/hooks/flight.query';
import useSearchBar from '@components/views/flight/hooks/useSearchBar';
import ResponsiveListingFilter from '@components/views/hotel/listing/components/ResponsiveFilterAndSort/ResponsiveListingFilterAndSort';
import { ButtonsWrapper } from '@components/views/hotel/components/SearchBar/components/ResponsiveListingBox/styled';
import { paramsToObject } from '@components/views/utils';

import { useSelectedLocationSearchPackage } from './useSelectedLocationSearchPackage';
import ResponsiveSearch from './ResponsiveSearch';
import CyprusTab from '../package-hotel-selection-tab';
import { useCalendarHoliday } from '@components/DatePicker/DateRangePicker/getCalendarHolidays';

const PackageNearestDateWarning = dynamic(
  () => import('@components/views/components/PackageNearestDateWarning')
);

interface ResponsiveListingBoxProps {
  suggestionLocation?: string;
  location?: SearchViewModel;
  hotelRoomsList?: Array<ListingSearchRoomInterface>;
  meta?: MetaViewModel;
  isModalVisible: boolean;
  toggleIsModalVisible: () => void;
  startDate?: DateType | null;
  endDate?: DateType | null;
  sticky?: boolean;
  onResearchClick?: () => void;
  hideModal?: () => void;
  shouldShallowPush?: boolean;
  detail?: boolean;
  packageTabChanged?: boolean;
  setPackageTabChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  shouldResearchPackage?: boolean;
  hotelFilters?: HotelListFiltersViewModel[];
  isFetching?: boolean;
  isExpediaHotels?: boolean;
  hotels?: (PackageListHotelViewModel[] | undefined)[];
  selectedTab?: string;
  setSelectedTab?: React.Dispatch<React.SetStateAction<string>>;
  currentPage?: number;
  isAirportSelectionClicked?: boolean;
  setIsAirportSelectionClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  isAirportDateSelectionStarted?: boolean;
  setisAirportDateSelectionStarted?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsRedirecting?: React.Dispatch<React.SetStateAction<boolean>>;
  isHoliday?: boolean;
}

export interface ListingMetaInterface {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  title?: string;
  description?: string;
  isHoliday?: boolean;
}

const PackageResponsiveListingBox = ({
  meta,
  isModalVisible,
  toggleIsModalVisible,
  startDate,
  endDate,
  sticky = true,
  hideModal,
  shouldShallowPush,
  location,
  detail,
  setPackageTabChanged,
  shouldResearchPackage,
  hotelFilters,
  isFetching,
  isExpediaHotels,
  hotels,
  currentPage,
  selectedTab,
  setSelectedTab,
  isAirportDateSelectionStarted,
  isAirportSelectionClicked,
  setIsAirportSelectionClicked,
  setisAirportDateSelectionStarted,
  onResearchClick,
  setIsRedirecting,
  isHoliday,
}: ResponsiveListingBoxProps) => {
  const { isDesktop } = useDevice();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const params = useParams();

  const [
    isPackageNearestDateWarningVisible,
    setIsPackageNearestDateWarningVisible,
  ] = useState(false);
  const {
    departureAirportSearchText,
    departureAirport,
    onDepartureAirportSelect,
    onDepartureAirportTextChange,
  } = useSearchBar();

  useEffect(() => {
    if (isAirportDateSelectionStarted) {
      toggleIsModalVisible();
    }
  }, [isAirportDateSelectionStarted]);

  useEffect(() => {
    if (!isModalVisible) {
      setIsAirportSelectionClicked?.(false);
      setisAirportDateSelectionStarted?.(false);
    }
  }, [isModalVisible]);

  const { data: departureAirportData } = useFindAirportByAirportCode(
    query.dep as string
  );

  useEffect(() => {
    if (!query.dep) {
      onDepartureAirportSelect(undefined);
      onDepartureAirportTextChange('');
    }
  }, [query]);

  useEffect(() => {
    if (departureAirportData) {
      const foundAirport = query?.depIsCity
        ? departureAirportData?.find(
            (airport) =>
              airport.airportCode === query.dep &&
              airport.isCity === (query.depIsCity === 'true')
          )
        : departureAirportData?.[0];
      onDepartureAirportSelect(foundAirport);
    }
  }, [departureAirportData]);

  const { setActiveLocation, setLocation, isStickySearch, setIsStickySearch } =
    useSearchBarContext();
  const { selectedLocation } = useSelectedLocationSearchPackage(
    isDesktop === false
  );
  const [affixedListingBox, setIsAffixedListingBox] = useState(false);
  const urlData = useSearchURL();

  const { data } = useQuery(
    [params],
    () =>
      callService(
        { service: 'SearchesService', method: 'searchesUrl', version: 'v3' },
        { s: params?.slug as string, isPackage: true }
      ),
    {
      initialData: typeof window === 'undefined' ? urlData : undefined,

      onSuccess: (data) => {
        if (data) {
          setActiveLocation(data);
        }
      },
    }
  );

  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);

  const parseRoomParam = (roomParam) => {
    if (!roomParam) {
      return {
        adults: 2,
        children: [],
        roomCount: 1,
      };
    }

    const roomArray = Array.isArray(roomParam) ? roomParam : [roomParam];

    let adults = 0;
    const children: string[] = [];
    const roomCount = roomArray.length;

    roomArray.forEach((room) => {
      const roomParts = room.split('_');
      const adultCount = parseInt(roomParts[0], 10) || 0;
      adults += adultCount;

      const childData = roomParts.slice(1);
      children.push(...(childData as string[]));
    });

    return {
      adults,
      children,
      roomCount,
    };
  };

  const roomInfo = parseRoomParam(query?.room);

  const roomSelectBoxLabel = ` ${roomInfo?.roomCount} Oda, ${
    roomInfo?.adults
  } Yetişkin ${
    roomInfo?.children.length > 0
      ? ', ' + roomInfo?.children.length + ' Çocuk'
      : ''
  }`;

  const { isValidated: isKocAilemValidate } = useKocAliemContext();

  useEffect(() => {
    if (!selectedLocation) return;

    setLocation(selectedLocation);
    setActiveLocation(selectedLocation);
  }, [selectedLocation]);

  const onKeyPress = debounce((e: KeyboardEvent<HTMLDivElement>): void => {
    const enterOrSpace =
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.which === 13 ||
      e.which === 32;
    if (enterOrSpace) {
      e.preventDefault();
      toggleIsModalVisible();
    }
  }, 200);
  const { data: initialData } = useCalendarHoliday();
  return (
    <>
      {isPackageNearestDateWarningVisible ? (
        <PackageNearestDateWarning
          onCloseModal={() => {
            setIsPackageNearestDateWarningVisible(false);
          }}
        />
      ) : null}

      <ResponsiveBoxAffix
        sticky={sticky}
        onChange={(affixed) => setIsAffixedListingBox(affixed)}
      >
        <SearchReservedId show={sticky && affixedListingBox} />
        {isModalVisible ? (
          <ResponsiveSearch
            initialData={initialData}
            setIsRedirecting={setIsRedirecting}
            detail={detail}
            isShowModal={isModalVisible}
            hideModal={hideModal || toggleIsModalVisible}
            onResearchClick={() => {
              onResearchClick?.();
              setPackageTabChanged?.(false);
            }}
            shouldShallowPush={shouldShallowPush}
            departureAirportSearchText={departureAirportSearchText}
            onDepartureAirportSelect={onDepartureAirportSelect}
            departureAirport={departureAirport}
            togglesPackageNearestDateWarningVisible={
              setIsPackageNearestDateWarningVisible
            }
            isAirportSelectionClicked={isAirportSelectionClicked}
            setIsAirportSelectionClicked={setIsAirportSelectionClicked}
            isAirportDateSelectionStarted={isAirportDateSelectionStarted}
            setisAirportDateSelectionStarted={setisAirportDateSelectionStarted}
            isHoliday={isHoliday}
          />
        ) : null}
        <ResponsiveBox>
          <ResponsiveBoxContainer>
            {detail ? (
              <CyprusTab
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                shouldResearchPackage={shouldResearchPackage}
              />
            ) : (
              <CyprusTab
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
              />
            )}

            <StickyElement onStickyStateChange={setIsStickySearch} max={300} />
            <div>
              {isDesktop ? (
                <Typography variant='Title5' color='BR300' weight='SemiBold'>
                  {data?.text ?? location?.text}
                </Typography>
              ) : (
                <Typography variant='caption3' color='NL800' weight='SemiBold'>
                  {data?.text ?? location?.text}
                </Typography>
              )}

              {meta && meta.totalCount > 0 ? (
                <Typography variant='caption3' color='NL800' weight='SemiBold'>
                  ({meta.totalCount})
                </Typography>
              ) : null}
            </div>

            <ResponsiveSearchBoxWrapper>
              <ResponsiveSearchBoxItem>
                {departureAirportSearchText ? (
                  <ResponsiveSearchBoxLabel>
                    <Typography variant='caption3' color='NL500'>
                      {departureAirportSearchText}
                    </Typography>
                  </ResponsiveSearchBoxLabel>
                ) : null}
                <ResponsiveSearchBoxLabel>
                  <Typography variant='caption3' color='NL500' as='div'>
                    {startDate
                      ? `${formatMomentDate(startDate)} ${
                          endDate ? `- ${formatMomentDate(endDate)}` : ''
                        }`
                      : null}
                  </Typography>
                </ResponsiveSearchBoxLabel>
                <ResponsiveSearchBoxSubLabel>
                  <Typography variant='caption3' color='NL500'>
                    {roomSelectBoxLabel}
                  </Typography>
                </ResponsiveSearchBoxSubLabel>
              </ResponsiveSearchBoxItem>
              <ButtonsWrapper>
                {detail ? null : (
                  <ResponsiveListingFilter
                    select='hotel'
                    hotelFilters={hotelFilters}
                    filters={hotelFilters}
                    isLoading={isFetching}
                    hotels={hotels?.[currentPage || 0]}
                    isExpedia={isExpediaHotels}
                    setIsRedirecting={setIsRedirecting}
                  />
                )}

                <ResponsiveSearchBoxEdit
                  role='button'
                  tabIndex={0}
                  onKeyPress={onKeyPress}
                  onClick={toggleIsModalVisible}
                >
                  <SearchIconWrapper>
                    <CustomIcon
                      icon={SearchIcon}
                      fontSize={16}
                      paletteColor='B300'
                    />
                  </SearchIconWrapper>
                  <ResponsiveSearchBoxEditLabel>
                    <Typography
                      variant='caption3'
                      weight='Regular'
                      color='B300'
                    >
                      Yeniden Ara
                    </Typography>
                  </ResponsiveSearchBoxEditLabel>
                </ResponsiveSearchBoxEdit>
              </ButtonsWrapper>
            </ResponsiveSearchBoxWrapper>
            {loyaltyType && isStickySearch && !isKocAilemValidate ? (
              <KocAilemBanner showInfoText={false} />
            ) : null}
          </ResponsiveBoxContainer>
        </ResponsiveBox>
      </ResponsiveBoxAffix>
    </>
  );
};

export default PackageResponsiveListingBox;
